import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { UPDATE_CLIENT_BY_PK } from '@/graphql/mutations/clients';
import { SET_USER_CIRCLE } from '@/graphql/mutations/users';
import { DISABLE_SHIPPER_RATE_SHOP_OVERRIDES } from '../graphql';

// Circle flag the ingest pipeline actually reads to auto-assign by consignee zip.
const AUTO_ASSIGN_BY_ZIP_FLAG = 'auto-assign-by-zip';

// Writes clients.shipper_type plus the two side effects that have to travel with it.
// The mutations are shared by every row, so their `loading` flags can't say which shipper is
// saving. `pendingIds` tracks that per client_id, so only the row being edited locks.
export const useShipperTypeUpdate = ({ onSuccess, onError } = {}) => {
    const [updateClient] = useMutation(UPDATE_CLIENT_BY_PK);
    const [disableOverrides] = useMutation(DISABLE_SHIPPER_RATE_SHOP_OVERRIDES);
    const [setCircle] = useMutation(SET_USER_CIRCLE);
    const [pendingIds, setPendingIds] = useState(() => new Set());

    const setPending = (client_id, pending) =>
        setPendingIds((prev) => {
            const next = new Set(prev);
            if (pending) next.add(client_id);
            else next.delete(client_id);
            return next;
        });

    const updateShipperType = async (client, next) => {
        const current = client?.shipper_type || 'MARKETPLACE';
        if (next === current) return;
        setPending(client.client_id, true);
        try {
            // Leaving Rate Shop disables this shipper's pricing overrides so stale,
            // shipper-driven rates don't keep applying under the new mode.
            if (current === 'RATE_SHOP' && next !== 'RATE_SHOP') {
                await disableOverrides({ variables: { client_id: client.client_id } });
            }
            await updateClient({
                variables: { client_id: client.client_id, update: { shipper_type: next } },
            });
            // shipper_type is only a label; the pipeline gates on the auto-assign-by-zip
            // circle, so entering/leaving AUTO_ASSIGN must flip it or the mode never runs.
            const user_id = client?.user?.user_id;
            if (user_id && (next === 'AUTO_ASSIGN' || current === 'AUTO_ASSIGN')) {
                await setCircle({
                    variables: { user_id, patch: { [AUTO_ASSIGN_BY_ZIP_FLAG]: next === 'AUTO_ASSIGN' } },
                });
            }
            onSuccess?.(next);
        } catch (err) {
            onError?.(err);
        } finally {
            setPending(client.client_id, false);
        }
    };

    return { updateShipperType, isUpdating: (client_id) => pendingIds.has(client_id) };
};
